import { useEffect, useState, useContext, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axiosInstance from "../api/axios";
import AuthContext from "../context/AuthContext";
import NotFound from "./NotFound";

// import PostOptionsModal from "./PostOptionsModal";
import PostComments from "./comments/PostComments";
import { Heart, Ellipsis, MessageCircle, Bookmark, Send, ArrowBigLeft } from "lucide-react";
import { useModal } from "./ModalManager";
import ReactionPickerButton from "./ReactionPickerButton";
import ReactionCounts from "./ReactionCounts";
import { useCountdown } from "../hooks/useCountdown";
import { formatPostTimestamp } from "../utils/formatPostTimestamp";
import MentionText from "./mentions/MentionText";

const DEFAULT_AVATAR = `${process.env.PUBLIC_URL}/logo512.png`;

export default function PostStatic() {
  const { token, user } = useContext(AuthContext);
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const expiresAt = post?.expires_at ?? null;
  const { label, isExpired } = useCountdown(expiresAt);
  const [notFound, setNotFound] = useState(false);
  const isReadOnly = post?.is_expired ?? false;
  // Also covers a post that expires while this page is open.
  const commentsReadOnly = isReadOnly || isExpired;
  const { openModal, closeTopModal } = useModal();

  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef(null);

  // ?comment=<id> shows that comment's focused conversation instead of the post
  // body and root comments. Each conversation is its own history entry, so the
  // browser Back button walks back through them.
  const [searchParams] = useSearchParams();
  const focusedCommentId = Number(searchParams.get("comment")) || null;

  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [focusedCommentId]);

  const openConversation = (commentId) => {
    navigate(
      { pathname: location.pathname, search: `?comment=${commentId}` },
      {
        // Keep backgroundLocation so the post stays an overlay over its feed.
        state: { ...location.state, commentDepth: (location.state?.commentDepth || 0) + 1 },
      }
    );
  };

  const closeConversation = () => {
    if (location.state?.commentDepth > 0) {
      navigate(-1); // back to the previous conversation, or to all comments
    } else {
      // Opened directly (e.g. a shared link): go to the post's full comments.
      navigate(
        { pathname: location.pathname, search: "" },
        { replace: true, state: location.state }
      );
    }
  };

  const handleClose = () => {
    navigate(location.state?.backgroundLocation?.pathname || "/", {
      state: {
        updatedPostId: post.id,
      },
    });
  };

  // get post data
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axiosInstance.get(`/posts/${id}/`);
        setPost(res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setNotFound(true);
          return;
        }

        console.error("Error fetching post", err);
      }
    };
    fetchPost();
  }, [id]);

  const handleDeletePost = async () => {
    try {
      await axiosInstance.delete(`/posts/${id}/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      closeTopModal();

      navigate(location.state?.backgroundLocation?.pathname || "/", {
        replace: true,
        state: {
          deletedPostId: Number(id),
        },
      });
    } catch (err) {
      console.error("Failed to delete post", err);
    }
  };

  const handleToggleLike = async () => {
    try {
      const res = await axiosInstance.post(`/posts/${id}/toggle_like/`, null, {
        headers: { Authorization: `Token ${token}` },
      });
      setPost((prev) => ({
        ...prev,
        is_liked: res.data.is_liked,
        likes_count: res.data.likes_count,
      }));
    } catch (err) {
      console.error("Failed to like/unlike:", err);
    }
  };

  const handleReacted = ({ my_reaction, reaction_counts }) => {
    setPost((prev) => ({
      ...prev,
      my_reaction,
      reaction_counts,
    }));
  };

  function getTimerStyles(percentLeft) {
    if (percentLeft > 75) {
      return "border-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.25)]";
    }
    if (percentLeft > 40) {
      return "border-yellow-300 shadow-[0_0_12px_rgba(250,204,21,0.25)]";
    }
    if (percentLeft > 15) {
      return "border-orange-300 shadow-[0_0_12px_rgba(251,146,60,0.3)]";
    }
    return "border-red-500 shadow-[0_0_14px_rgba(239,68,68,0.35)] animate-pulse";
  }

  function getTimeLeftPercent(expiresAt) {
    if (!expiresAt) return 0;
    const now = Date.now();
    const end = new Date(expiresAt).getTime();
    const total = 24 * 60 * 60 * 1000; // example: 24h lifetime
    const remaining = Math.max(0, end - now);

    return Math.max(0, Math.min(100, (remaining / total) * 100));
  }

  function getCountdownTextStyles(percentLeft) {
    if (percentLeft > 75) return "text-cyan-500";
    if (percentLeft > 40) return "text-yellow-500";
    if (percentLeft > 15) return "text-orange-500";
    return "text-red-500";
  }

  // Explicit backend field — do not infer "indefinite" from !expires_at.
  const isIndefinite = post?.expiration_type === "indefinite";
  const percentLeft = getTimeLeftPercent(post?.expires_at ?? null);
  // expires_at is null for indefinite posts, which getTimeLeftPercent treats
  // as 0% time left — give indefinite posts the calm/neutral (100%) treatment
  // instead of the "about to expire" red border.
  const timerStyles = isReadOnly
    ? "border-gray-300"
    : isIndefinite
      ? getTimerStyles(100)
      : getTimerStyles(percentLeft);

  const textStyles = isReadOnly ? "text-gray-500" : getCountdownTextStyles(percentLeft);

  if (notFound) return <NotFound />;
  if (!post) return <div>Loading...</div>;

  return (
    <div ref={scrollContainerRef} className="fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="h-full flex">
        {/* Spacer that matches sidebar widths */}
        <div className="hidden sm:block ml-9 sm:w-20 xl:w-56" />

        {/* Main content area */}
        <div className="flex-1 flex justify-center">
          <div className="max-w-2xl w-full px-4 py-6 relative">
            {/* Close / Back */}
            <button onClick={handleClose} className="absolute top-7 -left-6 text-gray-600">
              <ArrowBigLeft />
            </button>
            {/* USER HEADER */}
            <div className="flex items-center mb-3">
              <button onClick={handleClose} className="flex pl-2 sm:hidden text-gray-600">
                <ArrowBigLeft />
              </button>
              {/* The post payload carries the author's avatar, same as comments. */}
              <img
                src={post.profile_pic_url || DEFAULT_AVATAR}
                onError={(e) => {
                  // Broken or missing image → default avatar (once; img.src is absolute).
                  const fallback = new URL(DEFAULT_AVATAR, window.location.href).href;
                  if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                }}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover border flex-shrink-0"
              />
              <p className="font-semibold ml-2">{post.username}</p>

              <button
                onClick={() =>
                  openModal("PostOptionsModal", {
                    post: post,
                    isOwner: post.username === user.username,
                    onDelete: handleDeletePost,
                  })
                }
                className="ml-auto text-gray-600"
              >
                <Ellipsis />
              </button>
            </div>

            {!focusedCommentId && (
              <>
                {/* MEDIA */}
                <div className={`w-full flex justify-center border-b border-t pt-2 pb-2 mb-4`}>
                  {post.media_type === "image" && (
                    <img
                      src={post.media_url}
                      alt={post.text}
                      className="w-full max-h-[70vh] object-contain rounded-md"
                    />
                  )}
                  {post.media_type === "video" && (
                    <video controls className="w-full max-h-[70vh] object-contain rounded-md">
                      <source src={post.media_url} type="video/mp4" />
                    </video>
                  )}
                  {post.media_type === "text" && (
                    <div className="w-full bg-white border rounded-md p-10 flex items-center justify-center min-h-[40vh]">
                      <p className="text-2xl font-medium text-center leading-snug whitespace-pre-wrap">
                        <MentionText text={post.text} mentions={post.mentions} />
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-1 flex-col w-full pb-1">
                  {/* text */}
                  <div className="pl-1">
                    {post?.text && post.media_type !== "text" && (
                      <div className="flex items-start gap-2 mb-6">
                        <p className="text-sm">
                          <strong>{post.username}</strong>{" "}
                          <MentionText text={post.text} mentions={post.mentions} />
                        </p>
                      </div>
                    )}
                  </div>

                  {/* tags */}
                  <div className="mt-auto flex flex-wrap gap-2 pt-2 pb-2">
                    {post.tags?.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => navigate(`/tags/${tag}`)}
                        className="text-sm px-2 py-1 rounded-full border"
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ACTIONS */}
                <div className="flex justify-between mb-3 p-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <button
                      onClick={post.is_expired ? undefined : handleToggleLike}
                      disabled={post.is_expired}
                      className={`flex items-center space-x-1 ${
                        post.is_expired
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-red-500 hover:text-red-600"
                      }`}
                    >
                      <Heart fill={!post.is_expired && post.is_liked ? "currentColor" : "none"} />
                      <span>{post.likes_count}</span>
                    </button>

                    <button
                      disabled={post.is_expired}
                      className={`flex items-center space-x-1 ${
                        post.is_expired ? "text-gray-400 cursor-not-allowed" : ""
                      }`}
                    >
                      <MessageCircle />
                      <span>{post.comment_count}</span>
                    </button>

                    <ReactionCounts reactionCounts={post.reaction_counts} />
                  </div>
                  {/* <button
                    className="flex"
                    onClick={() => openModal("UserSelectModal", { post, token })}
                  >
                    <Send />
                  </button> */}
                  <div className="flex items-center gap-2">
                    <ReactionPickerButton
                      postId={post.id}
                      token={token}
                      myReaction={post.my_reaction}
                      onReacted={handleReacted}
                      disabled={post.is_expired}
                    />
                    <button
                      className={`flex ${post.is_expired ? "text-gray-400 cursor-not-allowed" : ""}`}
                      onClick={
                        post.is_expired
                          ? undefined
                          : () => {
                              openModal("ShareModal", { post });
                            }
                      }
                      disabled={post.is_expired}
                    >
                      <Send />
                    </button>
                  </div>
                </div>

                {/* TIMESTAMP */}
                <div className={`flex mb-2 w-full border-t border-b space-x-1 ${timerStyles}`}>
                  {post.is_expired ? (
                    <div className="flex items-center pt-2 pb-2">
                      <p className="text-xs text-gray-500 font-medium">Expired</p>
                    </div>
                  ) : isIndefinite ? (
                    <div className="flex items-center pt-2 pb-2">
                      <p className="text-xs text-gray-500">{formatPostTimestamp(post.created_at)}</p>
                    </div>
                  ) : (
                    <div className="flex space-x-1 items-center pt-2 pb-2">
                      <p className="text-xs gray-500">Expires in:</p>
                      <p className={`text-xs ${textStyles}`}>{label}</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* COMMENTS */}
            <PostComments
              post={post}
              readOnly={commentsReadOnly}
              focusedCommentId={focusedCommentId}
              onOpenConversation={openConversation}
              onCloseConversation={closeConversation}
              onCommentCountChange={(count) =>
                setPost((prev) => ({ ...prev, comment_count: count }))
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
